import { AfterContentChecked, Directive, ElementRef, Input, Renderer2, effect, inject } from '@angular/core';
import { ScreenSize } from '../enums';
import { LayoutBreakpoint, VisualLayout, LayoutWidthBreakpoints } from '../interfaces';
import { LayoutService } from '../services';

@Directive({
    selector: '[caLayout]'
})
export class LayoutDirective implements AfterContentChecked {
    @Input() caLayout: VisualLayout | undefined;
    @Input() caLayoutGap = 16;
    private readonly renderer = inject(Renderer2);
    private readonly elementRef = inject(ElementRef);
    private readonly layoutService = inject(LayoutService);
    private readonly screenSize = effect(() => {
        const screenSize = this.layoutService.screenSize();
        this.applyLayoutStyles(screenSize);
    });

    ngAfterContentChecked(): void {
        this.applyLayoutStyles(this.layoutService.screenSize());
    }

    private applyLayoutStyles(screenSize: ScreenSize) {
        if (!this.caLayout) {
            this.setDefaultLayout();
            return;
        }

        const layoutConfig: LayoutBreakpoint =
            this.caLayout.breakpoints![screenSize.toLowerCase() as keyof typeof this.caLayout.breakpoints];

        if (!layoutConfig) {
            return;
        }

        const children = this.elementRef.nativeElement.children as ElementRef[];
        const columnConfig = layoutConfig.columns || [];
        const numColumns = columnConfig.length;

        this.setDefaultLayout();

        const totalGap = this.caLayoutGap * (numColumns - 1);
        const totalSize = columnConfig.reduce((sum, col) => sum + col.size!, 0);

        Array.from(children).forEach((child: any, index: number) => {
            const columnIndex = index % numColumns;
            const column = columnConfig[columnIndex];
            const flexBasis = `calc(${(column.size! / totalSize) * 100}% - ${totalGap / numColumns}px)`;

            this.renderer.setStyle(child, 'flex-basis', flexBasis);
            this.renderer.setStyle(child, 'flex-grow', '0');
            this.renderer.setStyle(child, 'flex-shrink', '0');
            this.renderer.setStyle(child, 'display', 'flex');
            this.renderer.setStyle(child, 'align-self', 'flex-start');

            const alignmentMap = {
                START: 'flex-start',
                CENTER: 'center',
                END: 'flex-end',
                STRETCH: 'stretch'
            };
            const alignment = column?.alignment?.toUpperCase() as 'START' | 'CENTER' | 'END' | 'STRETCH';

            if (alignment === 'STRETCH') {
                Array.from(child.children).forEach((grandChild: any) => {
                    this.renderer.addClass(grandChild, 'stretch');
                });
            }

            this.renderer.setStyle(child, 'align-items', alignmentMap[alignment] ?? 'stretch');
            this.renderer.setStyle(child, 'justify-content', alignmentMap[alignment] ?? 'stretch');
        });
    }

    setDefaultLayout() {
        this.renderer.setStyle(this.elementRef.nativeElement, 'display', 'flex');
        this.renderer.setStyle(this.elementRef.nativeElement, 'flex-wrap', 'wrap');
        this.renderer.setStyle(this.elementRef.nativeElement, 'gap', `${this.caLayoutGap}px`);
    }
}
